// src/dashboard/pages/teacher/Objectifs/utils/objectifsDashboardUtils.js

export const OBJECTIF_TYPES = [
  "loisir",
  "ceinture",
  "poids",
  "renforcement",
  "competition",
  "kata",
  "uv2",
  "uv3_points",
  "autre",
];

export const OBJECTIF_TYPE_LABELS = {
  loisir: "Loisir",
  ceinture: "Ceinture",
  poids: "Poids",
  renforcement: "Renforcement",
  competition: "Compétition",
  kata: "Kata",
  uv2: "UV2",
  uv3_points: "UV3 points",
  autre: "Autre",
};

export function normalizeString(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function seasonLabel(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const start = month >= 8 ? year : year - 1;

  return `${start}-${start + 1}`;
}

export function seasonNeighbors(active) {
  const [start, end] = String(active || "")
    .split("-")
    .map(Number);

  if (!start || !end) {
    return {
      prev: "",
      next: "",
    };
  }

  return {
    prev: `${start - 1}-${end - 1}`,
    next: `${start + 1}-${end + 1}`,
  };
}

export function getSeasonOptionsFromActive(activeSeason) {
  const active = activeSeason || seasonLabel();
  const { prev, next } = seasonNeighbors(active);

  return [prev, active, next].filter(Boolean);
}

export function formatDateFr(date) {
  if (!date) return "—";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return "—";

  return parsed.toLocaleDateString("fr-FR");
}

export function toDateInputValue(date) {
  if (!date) return "";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toISOString().slice(0, 10);
}

export function clampProgress(value) {
  return Math.max(0, Math.min(100, Number(value) || 0));
}

export function getObjectifAdherent(objectif) {
  return objectif?.adherent || objectif?.Adherent || objectif?.adherentData || {};
}

export function getAdherentGroups(adherent) {
  return adherent?.Groups || adherent?.groups || [];
}

export function getObjectifGroups(objectif) {
  const adherent = getObjectifAdherent(objectif);
  return getAdherentGroups(adherent);
}

export function getAdherentDisplayName(adherent) {
  const nom = String(adherent?.nom || "").trim();
  const prenom = String(adherent?.prenom || "").trim();

  const fullName = `${nom.toUpperCase()} ${prenom}`.trim();

  return fullName || "Adhérent non renseigné";
}

export function getObjectifAdherentName(objectif) {
  return getAdherentDisplayName(getObjectifAdherent(objectif));
}

export function sortAdherents(a, b) {
  return getAdherentDisplayName(a).localeCompare(getAdherentDisplayName(b), "fr", {
    sensitivity: "base",
  });
}

export function sortObjectifs(rows = []) {
  return [...rows].sort((a, b) => {
    const dateA = new Date(a?.createdAt || a?.id || 0);
    const dateB = new Date(b?.createdAt || b?.id || 0);

    return dateB - dateA;
  });
}

export function filterAdherentsByGroup(adherents = [], groupName = "") {
  if (!groupName) return adherents;

  return adherents.filter((adherent) =>
    getAdherentGroups(adherent).some((group) => group.nom === groupName)
  );
}

export function filterObjectifs(rows = [], filters = {}) {
  const query = normalizeString(filters.query);
  const group = String(filters.group || "");
  const adherentId = String(filters.adherentId || "");
  const type = String(filters.type || "");
  const status = String(filters.status || "");

  return rows.filter((row) => {
    const adherent = getObjectifAdherent(row);
    const groups = getAdherentGroups(adherent);

    const searchable = normalizeString(
      [
        row?.title,
        row?.description,
        row?.type,
        row?.saison,
        getAdherentDisplayName(adherent),
        groups.map((group) => group.nom).join(" "),
      ]
        .filter(Boolean)
        .join(" ")
    );

    const currentAdherentId = row?.adherent_id || adherent?.id;

    const matchesQuery = !query || searchable.includes(query);
    const matchesGroup =
      !group || groups.some((currentGroup) => currentGroup.nom === group);
    const matchesAdherent =
      !adherentId || String(currentAdherentId) === String(adherentId);
    const matchesType = !type || row?.type === type;
    const matchesStatus =
      !status ||
      (status === "done" && !!row?.is_done) ||
      (status === "todo" && !row?.is_done);

    return (
      matchesQuery &&
      matchesGroup &&
      matchesAdherent &&
      matchesType &&
      matchesStatus
    );
  });
}

export function calculateObjectifsStats(allRows = [], filteredRows = []) {
  const done = allRows.filter((row) => !!row?.is_done).length;
  const todo = allRows.length - done;

  const progressAverage =
    allRows.length > 0
      ? Math.round(
          allRows.reduce((sum, row) => sum + clampProgress(row?.progress), 0) /
            allRows.length
        )
      : 0;

  const adherents = new Set();
  const types = new Set();

  allRows.forEach((row) => {
    const adherentId = row?.adherent_id || getObjectifAdherent(row)?.id;
    if (adherentId) adherents.add(String(adherentId));
    if (row?.type) types.add(row.type);
  });

  return {
    total: allRows.length,
    filtered: filteredRows.length,
    done,
    todo,
    progressAverage,
    adherents: adherents.size,
    types: types.size,
  };
}

export function buildObjectifPayload(formData = {}) {
  const progress = clampProgress(formData.progress);
  const isDone = !!formData.is_done || progress >= 100;

  return {
    adherent_id: formData.adherent_id ? Number(formData.adherent_id) : null,
    saison: formData.saison || null,
    title: String(formData.title || "").trim(),
    description: String(formData.description || "").trim() || null,
    type: formData.type || null,
    progress,
    is_done: isDone,
    start_date: formData.start_date || null,
    due_date: formData.due_date || null,
  };
}